import { hasNoCreditsLeft } from 'components/CreationForms/utils/credits';
import { TableType } from 'crono-fe-common/types/externalProperty';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import { useState } from 'react';

const EMPTY_IDS: number[] = [];

/**
 * The AI fields a creation form generates for the new records. It starts from
 * the fields set to generate on insert; a pick only applies to the table type
 * it was made for, so switching Contact / Lead falls back to that type's
 * defaults instead of sending the other type's fields. Without AI credits left
 * nothing is generated, as nothing can be picked.
 *
 * @param creditsLeft AI credits left, undefined while they load.
 */
export const useAiFieldsToEnrich = (
  tableType: TableType,
  creditsLeft: number | null | undefined,
) => {
  const { data } = useGetExternalProperty({ tableType, onlyAiVariables: true });
  const aiFields = data?.data?.data;
  const noCreditsLeft = hasNoCreditsLeft(creditsLeft);

  const [picked, setPicked] = useState<{
    tableType: TableType;
    ids: number[];
  } | null>(null);

  const hasAiFields = !!aiFields?.length;

  let defaultIds = EMPTY_IDS;
  if (hasAiFields && !noCreditsLeft) {
    defaultIds = aiFields
      .filter((field) => field.generateAiVariablesOnInsert)
      .map((field) => field.id);
  }

  let selectedIds = defaultIds;
  if (noCreditsLeft) {
    selectedIds = EMPTY_IDS;
  } else if (picked?.tableType === tableType) {
    selectedIds = picked.ids;
  }

  const setSelectedIds = (ids: number[]) => setPicked({ tableType, ids });

  return {
    hasAiFields,
    selectedIds,
    setSelectedIds,
    /** The user picked other fields than the defaults. */
    isDirty:
      selectedIds.length !== defaultIds.length ||
      selectedIds.some((id) => !defaultIds.includes(id)),
  };
};
